import React, { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { unselectUser } from 'redux/users/actions';
import { getSelectedUser } from 'redux/users/selectors';
import { withStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import { bindAll, debounce, get } from 'lodash';
import api from 'api';
import { ASC, DESC } from 'config/constants';

import { getEventName, getEventSort, createDefaultState, getFetchFunction, cancelRequest } from './config';
import { EVENTS, defaultEvent } from '../Users.config';
import { styles, Main, Columns } from './styles';

import Header from './Header';
import UserInfo from './UserInfo';
import Events from './Events';

const baseState = {
  selectedEvent: defaultEvent.id,
  fetchingData: false,
  fetchingDataFailure: false,
  nextPage: null,
  showProgressScroll: false,
  selectedUser: null,
};

const defaultState = createDefaultState(baseState);

class Details extends Component {
  constructor(props) {
    super(props);

    this.lastRequest = null;
    this.allowInfiniteScroll = true;
    this.showProgressScroll = false;
    this.state = {
      ...defaultState,
      didMount: false,
    };

    bindAll(this, [
      'handleViewChange',
      'fetchEventDataSuccess',
      'fetchEventDataFailure',
      'initScrollEvent',
      'updateDataSuccess',
      'updateDataFailure',
      'userDataSuccess',
      'sortTable',
    ]);
    this.infiniteScroll = debounce(this.infiniteScroll.bind(this), 5);
  }

  componentDidMount() {
    this.setState({ didMount: true }, () => this.fetchEventData(defaultEvent.id));
  }

  componentDidUpdate(prevProps) {
    if (this.didClose(prevProps)) {
      this.resetUrl();
      this.resetState();
    }

    if (this.shouldFetchEventData(prevProps)) {
      this.changeLink();
      this.initUserData();
    }
  }

  changeLink(forceViewName) {
    const { history } = this.props;
    const linkUserId = get(this.props, 'match.user_id');
    const user_id = get(this.props, 'initUserData.user_id');
    const eventName = get(this.props, 'initUserData.eventName');

    if (linkUserId) return false;

    const url = this.generateLink(user_id, eventName, forceViewName);
    this.setTagIdFromLink();
    history.push(url);
  }

  setTagIdFromLink() {
    this.urlSearch = this.props.location.search;
  }

  getTagIdFromLink() {
    return this.urlSearch;
  }

  resetTagIdAfterClose() {
    this.urlSearch = '';
  }

  generateLink(user_id, eventName, forceViewName) {
    const linkParts = ['/console/users'];
    linkParts.push(user_id);
    linkParts.push(this.getViewName(eventName, forceViewName));
    return linkParts.join('/');
  }

  getViewName(eventName, forceViewName) {
    if (forceViewName) return forceViewName;
    else if (eventName) return eventName;
    return defaultEvent.id;
  }

  resetUrl() {
    const { history } = this.props;
    const tagId = this.getTagIdFromLink();
    history.push(`/console/users${tagId}`);
    this.resetTagIdAfterClose();
  }

  sortTable(event) {
    const currentTarget = get(event, 'currentTarget');
    const isSortable = get(currentTarget, 'dataset.isSortable');
    const sortField = get(currentTarget, 'dataset.sortField');
    if (isSortable === 'true') this.setEventSort(sortField);
  }

  setEventSort(sortField) {
    const { selectedEvent } = this.state;
    const eventSortName = getEventSort(selectedEvent);
    const eventSort = this.state[eventSortName];
    const order = eventSort.order === ASC ? DESC : ASC;
    const newEventSort = { order, field: sortField };
    this.setState({ [eventSortName]: newEventSort }, () => this.fetchEventData(selectedEvent));
  }

  shouldFetchEventData(prevProps) {
    return this.didOpen(prevProps) && this.state.didMount;
  }

  didOpen(prevProps) {
    const { open: prevOpen } = prevProps;
    const { open: currentOpen } = this.props;
    return currentOpen && !prevOpen;
  }

  didClose(prevProps) {
    const { open: prevOpen } = prevProps;
    const { open: currentOpen } = this.props;
    return !currentOpen && prevOpen;
  }

  initUserData() {
    const { initUserData } = this.props;

    api.getUserData({
      user_id: initUserData.user_id,
      cb: this.userDataSuccess,
    });
  }

  userDataSuccess(response) {
    const selectedUser = get(response, 'data.users[0]');
    this.setState({ selectedUser }, this.initViewFromUserData);
  }

  initViewFromUserData() {
    const eventName = get(this.props, 'initUserData.eventName', '');
    const event = EVENTS[eventName];

    if (event) this.handleViewChange(event.id);
    else this.handleViewChange(defaultEvent.id);
  }

  resetState() {
    this.setState(defaultState, () => {
      this.lastRequest = null;
      this.allowInfiniteScroll = true;
    });
  }

  handleViewChange(selectedEvent) {
    if (this.lastRequest) cancelRequest(this.lastRequest);
    this.allowInfiniteScroll = true;
    this.setState({ selectedEvent, nextPage: null }, () => {
      this.changeLink(selectedEvent);
    });
    this.fetchEventData(selectedEvent);
  }

  fetchEventData(type) {
    const fetchData = getFetchFunction(type);
    const user_id = get(this.state, 'selectedUser.user_id');
    const sort = get(this.state, `${getEventSort(type)}.order`);
    if (!user_id) return false;

    this.setState({ fetchingData: true });

    fetchData({ user_id, sort })
      .then(response => this.fetchEventDataSuccess(response, type))
      .catch(this.fetchEventDataFailure);
  }

  fetchEventDataSuccess(response, type) {
    const data = get(response, `data.${type}`, []);
    const next = get(response, 'data.pages.next', null);
    const nextPage = api.extractEndpointFromLink(next);
    const showProgressScroll = !!next;

    this.setState({
      [getEventName(type)]: data,
      fetchingData: false,
      fetchingDataFailure: false,
      nextPage,
      showProgressScroll,
    }, this.initScrollEvent);
  }

  fetchEventDataFailure() {
    this.setState({ fetchingDataFailure: true, fetchingData: false });
  }

  infiniteScroll() {
    const { nextPage } = this.state;

    if (this.isScrollingDisabled(nextPage)) return false;
    this.allowInfiniteScroll = false;
    this.updateData();
  }

  isScrollingDisabled(nextPage) {
    const scrollBottomPosition = this.scrollContent.scrollTop + this.scrollContent.clientHeight;
    const contentHeight = (this.scrollContent.scrollHeight);
    const loadPropertiesHeight = contentHeight * 0.8;

    return (scrollBottomPosition < loadPropertiesHeight) || !this.allowInfiniteScroll || !nextPage;
  }

  initScrollEvent() {
    this.scrollContent = document.querySelector('.js-DetailsEvents__scroll');
    if (this.scrollContent) this.scrollContent.addEventListener('scroll', this.infiniteScroll);
  }

  updateData() {
    const { nextPage } = this.state;

    const { cancelToken } = api.request({
      url: nextPage,
      cb: this.updateDataSuccess,
      cbError: this.updateDataFailure,
      withCancel: true,
    });

    this.lastRequest = cancelToken;
  }

  updateDataSuccess(response) {
    if (!response) return false;

    const { selectedEvent } = this.state;
    const prevData = this.state[getEventName(selectedEvent)];
    const newData = get(response, `data.${selectedEvent}`, []);
    const next = get(response, 'data.pages.next', null);
    const nextPage = api.extractEndpointFromLink(next);
    const updatedData = [...prevData, ...newData];
    const showProgressScroll = !!next;

    this.setState({
      [getEventName(selectedEvent)]: updatedData,
      nextPage,
      showProgressScroll,
    }, () => {
      this.allowInfiniteScroll = true;
    });
  }

  updateDataFailure() {
    this.setState({ showProgressScroll: false });
  }

  render() {
    const {
      open,
      handleCloseDrawer,
      classes,
      initUserData,
    } = this.props;

    const {
      selectedEvent,
      selectedUser,
      fetchingData,
      fetchingDataFailure,
      showProgressScroll,
    } = this.state;

    const eventsData = this.state[getEventName(selectedEvent)];
    const eventsSort = this.state[getEventSort(selectedEvent)];
    const userId = get(initUserData, 'user_id', '');

    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={handleCloseDrawer}
        classes={{ paper: classes.drawerPaper }}
      >
        <Main>
          <Header
            selectedEvent={selectedEvent}
            handleViewChange={this.handleViewChange}
            handleCloseDrawer={handleCloseDrawer}
            user_id={userId}
          />

          <Columns>
            <UserInfo selectedUser={selectedUser} />

            <Events
              eventsData={eventsData}
              eventsSort={eventsSort}
              sortTable={this.sortTable}
              selectedEvent={selectedEvent}
              fetchingData={fetchingData}
              fetchingDataFailure={fetchingDataFailure}
              initScrollEvent={this.initScrollEvent}
              showProgressScroll={showProgressScroll}
            />
          </Columns>
        </Main>
      </Drawer>
    );
  }
}

const mapStateToProps = state => {
  return {
    initUserData: getSelectedUser(state),
    open: !!getSelectedUser(state),
  };
};

const mapDispatchToProps = dispatch => {
  return {
    handleCloseDrawer: () => dispatch(unselectUser()),
  };
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withRouter,
  withStyles(styles),
)(Details);



// WEBPACK FOOTER //
// ./src/components/IterioApp/Users/Details/Details.jsx